import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApi } from '../api';
import { downloadFile, transformSnake2Camel } from './utils';

// Per the export_requests API notes: processing represents both queued-for-worker and actively generating.
export const EXPORT_REQUEST_STATUS = {
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  EXPIRED: 'expired'
};

const POLLING_INTERVAL_MS = 5000;

export const EXPORT_CONTENT = {
  GENERAL: 'general',
  FORMULARY_RESPONSES: 'formulary_responses'
};

export const SUMMARY_LEVEL = {
  NONE: 'none',
  BASIC: 'basic',
  ANALYTICS: 'analytics'
};

export const MAXIMUM_EXPORT_SIZE_EXCEEDED = 'maximum_export_size_exceeded';

export const readExportRequestError = error => {
  const apiError = ((error || {}).response || {}).data || {};

  return transformSnake2Camel(apiError.error || {});
};

// Staging contract (2026-07-29): POST body is wrapped in export_request, with resource/format at the top level.
// `preferences` shape depends on scope — a single-formulary export unlocks the formulary_responses fields
// (evidence counts/analytics/details), while a multi-formulary export only carries a summary_level.
export const useCreateExportRequest = ({ clientId, onSuccess = () => {} } = {}) =>
  useMutation(
    ({
      resource,
      format = 'xlsx',
      projectIds,
      formularyIds = [],
      inspectedAtRange,
      published,
      type,
      query,
      content,
      summaryLevel,
      includeEvidenceCounts,
      includeAnalyticsSheet,
      includeEvidenceDetails
    }) =>
      getApi()
        .post(`/v1/clients/${clientId}/export_requests`, {
          export_request: {
            resource,
            format,
            filters: {
              ...(projectIds && projectIds.length ? { project_ids: projectIds } : {}),
              ...(formularyIds.length ? { formulary_ids: formularyIds } : {}),
              ...(inspectedAtRange ? { inspected_at: { from: inspectedAtRange.startDate, to: inspectedAtRange.endDate } } : {}),
              ...(published !== null && published !== undefined ? { published: Boolean(published) } : {}),
              ...(type ? { inspection_type: type } : {}),
              ...(query ? { query } : {})
            },
            preferences:
              content === EXPORT_CONTENT.FORMULARY_RESPONSES
                ? {
                    content: EXPORT_CONTENT.FORMULARY_RESPONSES,
                    include_evidence_counts: includeEvidenceCounts,
                    include_analytics_sheet: includeAnalyticsSheet,
                    include_evidence_details: includeEvidenceDetails,
                    summary_level: summaryLevel
                  }
                : {
                    content: EXPORT_CONTENT.GENERAL,
                    summary_level: summaryLevel
                  }
          }
        })
        .then(axiosResult => transformSnake2Camel(axiosResult.data)),
    { onSuccess }
  );

const LIST_REFRESH_INTERVAL_MS = 60000;

export const useGetExportRequests = ({ clientId, enabled = true, refetchInBackground = false }) => {
  const queryFn = async context => {
    const [{ clientId: contextClientId }] = context.queryKey;
    const result = await getApi().get(`/v1/clients/${contextClientId}/export_requests`);

    return transformSnake2Camel(result.data);
  };

  return useQuery({
    queryKey: [{ clientId }, 'GET_exportRequests'],
    queryFn,
    enabled: enabled && !!clientId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...(refetchInBackground ? { refetchInterval: LIST_REFRESH_INTERVAL_MS, refetchIntervalInBackground: true } : {})
  });
};

// Polls GET /:id every 5s while the request is processing; the API notes require polling to stop on completed/failed/expired
// or when the consuming component unmounts — react-query handles the unmount case automatically.
export const useGetExportRequest = ({ clientId, exportRequestId, enabled = true }) => {
  const queryFn = async context => {
    const [{ clientId: contextClientId, exportRequestId: contextExportRequestId }] = context.queryKey;
    const result = await getApi().get(`/v1/clients/${contextClientId}/export_requests/${contextExportRequestId}`);

    return transformSnake2Camel(result.data);
  };

  return useQuery({
    queryKey: [{ clientId, exportRequestId }, 'GET_exportRequest'],
    queryFn,
    enabled: enabled && !!clientId && !!exportRequestId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: data => (data && data.status === EXPORT_REQUEST_STATUS.PROCESSING ? POLLING_INTERVAL_MS : false)
  });
};

// The download endpoint requires the same auth headers as any other API call, so we fetch it as a blob
// (like useDownloadBeneficiaryDocument) instead of window.open — a plain navigation wouldn't carry those headers.
export const useDownloadExportRequest = () =>
  useMutation(({ clientId, exportRequestId, filename }) =>
    getApi()
      .get(`/v1/clients/${clientId}/export_requests/${exportRequestId}/download`, { responseType: 'blob' })
      .then(response => downloadFile(response.data, filename))
  );

export const useGetExportRequestsSummary = ({ clientId }) => {
  const queryFn = async context => {
    const [{ clientId: contextClientId }] = context.queryKey;
    const result = await getApi().get(`/v1/clients/${contextClientId}/export_requests/summary`);

    return transformSnake2Camel(result.data);
  };

  return useQuery({
    queryKey: [{ clientId }, 'GET_exportRequestsSummary'],
    queryFn,
    enabled: !!clientId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
};

export const useMarkExportRequestsAsSeen = ({ clientId } = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    () =>
      getApi()
        .patch(`/v1/clients/${clientId}/export_requests/mark_as_seen`)
        .then(axiosResult => transformSnake2Camel(axiosResult.data)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([{ clientId }, 'GET_exportRequestsSummary']);
      }
    }
  );
};
